/**
 * DNACore V2 — Design System primitives
 * Use estes componentes em todas as telas novas para garantir consistência visual.
 *
 * Exemplo:
 *   import { PageHeader, SectionCard, StatusBadge } from '@/components/ui-dna';
 */
import React from 'react';
import { X } from 'lucide-react';

/** PageHeader — mesmo header gradient violeta do StudentWizard.
 *  Use no topo de qualquer tela ou modal grande de fluxo.
 */
export const PageHeader = ({
  icon: Icon,
  eyebrow,
  title,
  subtitle,
  progress, // 0..100 (opcional → mostra progress bar com glow)
  steps,    // [{ id, label, icon }] (opcional → renderiza stepper)
  activeStepIndex = 0,
  onStepClick,
  rightSlot,
  onClose,
  compact = false,
}) => (
  <div className={`dna-header-gradient relative overflow-hidden px-6 sm:px-8 ${compact ? 'pt-4 pb-3' : 'pt-5 pb-4'}`}>

    <div className="relative flex items-center justify-between gap-3">
      <div className="flex items-center gap-3.5 min-w-0">
        {Icon && (
          <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20 shadow-lg shrink-0">
            <Icon className="w-6 h-6" />
          </div>
        )}
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-violet-100/90 font-semibold truncate">
              {eyebrow}
            </p>
          )}
          {title && <h2 className="text-xl font-bold leading-tight truncate">{title}</h2>}
          {subtitle && <p className="text-[12px] text-violet-100/85 mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {rightSlot}
        {onClose && (
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/15 transition-colors" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>

    {progress !== undefined && (
      <div className="relative mt-5 h-2 dna-progress-track">
        <div className="absolute inset-y-0 left-0 dna-progress-fill" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
      </div>
    )}

    {Array.isArray(steps) && steps.length > 0 && (
      <div className="relative mt-3 flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {steps.map((s, i) => {
          const StepIcon = s.icon;
          const active = i === activeStepIndex;
          const done = i < activeStepIndex;
          const clickable = !!onStepClick && done;
          return (
            <button
              key={s.id || i}
              onClick={() => clickable && onStepClick(i)}
              className={`shrink-0 text-[10.5px] uppercase tracking-wider px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                active ? 'bg-white text-violet-700 font-bold shadow-md' :
                done ? 'text-emerald-200 hover:text-white cursor-pointer hover:bg-white/10' :
                'text-violet-200/70 cursor-default'
              }`}
              data-testid={`dna-step-${s.id || i}`}
            >
              {StepIcon && <StepIcon className="w-3 h-3" />}
              <span className="hidden sm:inline">{s.label}</span>
            </button>
          );
        })}
      </div>
    )}
  </div>
);

/** SectionCard — card branco padrão (rounded-2xl, sombra suave). */
export const SectionCard = ({ as: Tag = 'div', className = '', children, padded = true, accent, ...rest }) => (
  <Tag
    className={`dna-card ${padded ? 'p-5' : ''} ${accent === 'violet' ? 'border-l-4 border-l-violet-500' : ''} ${accent === 'emerald' ? 'border-l-4 border-l-emerald-500' : ''} ${className}`}
    {...rest}
  >
    {children}
  </Tag>
);

/** StatusBadge — pílula colorida padrão. */
export const StatusBadge = ({ tone = 'info', icon: Icon, children, className = '' }) => {
  const cls = ({
    success: 'dna-badge--success',
    warn: 'dna-badge--warn',
    danger: 'dna-badge--danger',
    info: 'dna-badge--info',
    neutral: '',
  })[tone] || '';
  return (
    <span className={`dna-badge ${cls} ${className}`}>
      {Icon && <Icon className="w-2.5 h-2.5" />}
      {children}
    </span>
  );
};

/** PageShell — wrapper de tela (área de conteúdo) com max-width e padding consistente. */
export const PageShell = ({ children, className = '' }) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 ${className}`}>
    {children}
  </div>
);

const _exports = { PageHeader, SectionCard, StatusBadge, PageShell };
export default _exports;
